import { useCallback, useEffect, useMemo, useState } from 'react';
import tesoreriaService, {
  GrupoCajaChica,
  MarcaTarjeta,
  MedioTransferencia,
  ReciboCreatePayload,
  ReciboMedioCobroCreate,
  appendMedioOtrosApiFields,
} from '../../../services/tesoreriaService';
import type { POSCobroDraft, POSLineaCobro, POSMedioCobroTipo, POSTarjetaCobro } from '../pos/types';
import { leerGrupoCajaGuardadoRaw, persistGrupoCajaChica } from '../../../utils/grupoCajaStorage';

const genId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const round2 = (n: number) => Math.round(n * 100) / 100;

export interface EstadoCobroPos {
  cobroHabilitado: boolean;
  cobroRequerido: boolean;
}

export const resolverEstadoCobro = (
  formaDePago: string,
  contadoPagoObligatorio: boolean,
): EstadoCobroPos => {
  const fp = String(formaDePago ?? '').trim().toUpperCase();
  if (fp === 'EFECTIVO') return { cobroHabilitado: true, cobroRequerido: true };
  if (fp === 'CONTADO') {
    return {
      cobroHabilitado: true,
      cobroRequerido: contadoPagoObligatorio,
    };
  }
  return { cobroHabilitado: false, cobroRequerido: false };
};

/** @deprecated Usar resolverEstadoCobro con contadoPagoObligatorio */
export const esCobroRequerido = (formaDePago: string): boolean =>
  resolverEstadoCobro(formaDePago, true).cobroRequerido;

const medioTransferenciaTieneCartera = (medio?: MedioTransferencia | null): boolean =>
  medio != null && Number(medio.cartera) > 0;

const labelMedio = (m: MedioTransferencia): string =>
  (m.descripcion || m.nombre || m.mcp || 'Medio').trim();

const initialTarjeta: POSTarjetaCobro = {
  codigoMarca: 0,
  numeroroAutorizacion: '',
  cuotas: 1,
};

const persistGrupoCaja = (grupo: GrupoCajaChica): void => {
  persistGrupoCajaChica(grupo);
};

const grupoEstaEnLista = (grupo: GrupoCajaChica, grupos: GrupoCajaChica[]): boolean =>
  grupos.some((g) => g.codigo === grupo.codigo || g.id === grupo.id);

const createDraft = (importe = 0): POSCobroDraft => ({
  tipo: 'efectivo',
  medioEfectivoId: null,
  medioTarjetaId: null,
  medioTransferenciaId: null,
  medioOtrosId: null,
  importe,
  tarjeta: { ...initialTarjeta },
});

const resolveMedioPorIndice = (
  medios: MedioTransferencia[],
  indice: number | string | null,
): MedioTransferencia | null => {
  const idx = Number(indice);
  if (
    indice != null &&
    Number.isFinite(idx) &&
    idx >= 0 &&
    idx < medios.length
  ) {
    return medios[idx];
  }
  return medios[0] ?? null;
};

const resolveMedioDraft = (
  draft: POSCobroDraft,
  mediosEfectivo: MedioTransferencia[],
  mediosTarjeta: MedioTransferencia[],
  mediosTransferencia: MedioTransferencia[],
  mediosOtros: MedioTransferencia[],
): MedioTransferencia | null => {
  if (draft.tipo === 'efectivo') {
    return resolveMedioPorIndice(mediosEfectivo, draft.medioEfectivoId);
  }
  if (draft.tipo === 'tarjeta') {
    return resolveMedioPorIndice(mediosTarjeta, draft.medioTarjetaId);
  }
  if (draft.tipo === 'transferencia') {
    return resolveMedioPorIndice(mediosTransferencia, draft.medioTransferenciaId);
  }
  if (draft.tipo === 'otros') {
    return resolveMedioPorIndice(mediosOtros, draft.medioOtrosId);
  }
  return null;
};

const lineaToMedioApi = (linea: POSLineaCobro): ReciboMedioCobroCreate => {
  const medio: ReciboMedioCobroCreate = {
    mcp: linea.mcp,
    importe: round2(linea.importe),
    detalle: linea.label,
  };
  if (linea.tipo === 'tarjeta' && linea.tarjeta) {
    medio.tarjeta = {
      codigoMarca: linea.tarjeta.codigoMarca,
      numeroroAutorizacion: String(linea.tarjeta.numeroroAutorizacion ?? '').trim(),
      cuotas: linea.tarjeta.cuotas ?? 1,
      fecha: new Date().toISOString(),
      fechaPresentar: new Date().toISOString(),
    };
  }
  if (linea.tipo === 'transferencia' && linea.cartera != null && linea.cartera >= 1000) {
    medio.cartera = linea.cartera;
  }
  if (linea.tipo === 'otros') {
    appendMedioOtrosApiFields(medio, {
      fechaRegistro: linea.fechaRegistro,
      nroComprobante: linea.nroComprobante,
    });
  }
  return medio;
};

/** Ajusta el último medio para que la suma coincida con el total de la factura (evita ±1¢ UI vs API). */
const ajustarMediosAlMontoFactura = (
  medios: ReciboMedioCobroCreate[],
  montoObjetivo: number,
): ReciboMedioCobroCreate[] => {
  if (medios.length === 0) return medios;
  const objetivo = round2(montoObjetivo);
  const suma = round2(medios.reduce((acc, m) => acc + (Number(m.importe) || 0), 0));
  const diff = round2(objetivo - suma);
  if (diff === 0) return medios;
  // Solo desfaces de centavos (misma tolerancia que validarCobro); no reescribir cobros distintos.
  if (Math.abs(diff) > 0.02) return medios;

  const ajustados = medios.map((m) => ({ ...m }));
  const last = ajustados.length - 1;
  const nuevoImporte = round2((Number(ajustados[last].importe) || 0) + diff);
  if (nuevoImporte < 0) return medios;

  ajustados[last] = {
    ...ajustados[last],
    importe: nuevoImporte,
  };
  return ajustados;
};

export interface UsePosCobroParams {
  total: number;
  formaDePago: string;
  contadoPagoObligatorio: boolean;
  nroCliente?: number;
  nroSuc: number;
  fechaEmision: string;
  /** Compañía de la factura: filtra MCPs TB cuya cartera tenga otra compañía. */
  idCompania?: string;
}

/** Medio TB visible si su cartera no tiene compañía o coincide con la de la factura. */
const medioTransferenciaCompatibleConCompania = (
  medio: MedioTransferencia,
  idCompaniaFactura: string,
): boolean => {
  const carteraCompania = String(medio.idCompania ?? '').trim();
  if (!carteraCompania) return true;
  return carteraCompania === idCompaniaFactura;
};

export function usePosCobro({
  total,
  formaDePago,
  contadoPagoObligatorio,
  nroCliente,
  nroSuc,
  fechaEmision,
  idCompania = '',
}: UsePosCobroParams) {
  const { cobroHabilitado, cobroRequerido } = resolverEstadoCobro(
    formaDePago,
    contadoPagoObligatorio,
  );

  const [grupoCaja, setGrupoCaja] = useState<GrupoCajaChica | null>(null);
  const [gruposUsuario, setGruposUsuario] = useState<GrupoCajaChica[]>([]);
  const [openSeleccionGrupo, setOpenSeleccionGrupo] = useState(false);
  const [grupoSeleccionPendienteId, setGrupoSeleccionPendienteId] = useState<number | null>(
    null,
  );
  const [loadingGrupo, setLoadingGrupo] = useState(false);
  const [mediosEfectivo, setMediosEfectivo] = useState<MedioTransferencia[]>([]);
  const [mediosTarjeta, setMediosTarjeta] = useState<MedioTransferencia[]>([]);
  const [mediosTransferencia, setMediosTransferencia] = useState<MedioTransferencia[]>([]);
  const [mediosOtros, setMediosOtros] = useState<MedioTransferencia[]>([]);
  const [loadingMedios, setLoadingMedios] = useState(false);
  const [marcasTarjeta, setMarcasTarjeta] = useState<MarcaTarjeta[]>([]);

  const [lineasCobro, setLineasCobro] = useState<POSLineaCobro[]>([]);
  const [draft, setDraft] = useState<POSCobroDraft>(() => createDraft(0));

  const idCompaniaNorm = String(idCompania ?? '').trim();

  const mediosTransferenciaFiltrados = useMemo(
    () =>
      mediosTransferencia.filter((m) =>
        medioTransferenciaCompatibleConCompania(m, idCompaniaNorm),
      ),
    [mediosTransferencia, idCompaniaNorm],
  );

  useEffect(() => {
    setDraft((prev) => ({
      ...prev,
      medioTransferenciaId: 0,
    }));
  }, [idCompaniaNorm]);

  const resolveGrupoFromStorage = useCallback((): GrupoCajaChica | null => {
    try {
      const saved = leerGrupoCajaGuardadoRaw();
      if (!saved) return null;
      const parsed = JSON.parse(saved);
      const codigo = Number(parsed?.codigo ?? parsed?.id ?? 0);
      if (!Number.isFinite(codigo) || codigo <= 0) return null;
      return {
        id: Number(parsed?.id ?? codigo),
        codigo,
        nombre: String(parsed?.nombre ?? `Grupo ${codigo}`),
      };
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingGrupo(true);
      try {
        const grupos = await tesoreriaService.getGruposCajaChica(
          nroCliente && nroCliente > 0 ? { nroCliente } : undefined,
        );
        if (cancelled) return;
        setGruposUsuario(grupos);

        const stored = resolveGrupoFromStorage();
        const storedValid =
          stored && grupoEstaEnLista(stored, grupos) ? stored : null;

        if (storedValid) {
          setGrupoCaja(storedValid);
          setOpenSeleccionGrupo(false);
          return;
        }

        if (grupos.length === 1) {
          persistGrupoCaja(grupos[0]);
          setGrupoCaja(grupos[0]);
          setOpenSeleccionGrupo(false);
          return;
        }

        if (grupos.length > 1) {
          setGrupoCaja(null);
          setGrupoSeleccionPendienteId(null);
          setOpenSeleccionGrupo(cobroRequerido);
          return;
        }

        setGrupoCaja(null);
        setOpenSeleccionGrupo(false);
      } catch {
        if (!cancelled) {
          setGruposUsuario([]);
          setGrupoCaja(null);
          setOpenSeleccionGrupo(false);
        }
      } finally {
        if (!cancelled) setLoadingGrupo(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [nroCliente, resolveGrupoFromStorage, cobroRequerido]);

  useEffect(() => {
    if (!cobroHabilitado || loadingGrupo) {
      if (!cobroHabilitado) setOpenSeleccionGrupo(false);
      return;
    }
    if (!cobroRequerido) {
      setOpenSeleccionGrupo(false);
      return;
    }
    if (grupoCaja?.codigo) {
      setOpenSeleccionGrupo(false);
      return;
    }
    if (gruposUsuario.length > 1) {
      setOpenSeleccionGrupo(true);
    }
  }, [cobroHabilitado, cobroRequerido, loadingGrupo, grupoCaja, gruposUsuario.length]);

  // CTA.CTE. / formas sin cobro: no conservar medios de un CONTADO/EFECTIVO previo.
  useEffect(() => {
    if (cobroHabilitado) return;
    setLineasCobro((prev) => (prev.length === 0 ? prev : []));
  }, [cobroHabilitado]);

  const confirmarGrupoSeleccion = useCallback(() => {
    const selected = gruposUsuario.find((g) => g.id === grupoSeleccionPendienteId);
    if (!selected) return;
    const cambioGrupo = grupoCaja?.codigo !== selected.codigo;
    persistGrupoCaja(selected);
    setGrupoCaja(selected);
    setOpenSeleccionGrupo(false);
    if (cambioGrupo) {
      setLineasCobro([]);
      const imp = round2(total > 0 ? total : 0);
      setDraft(createDraft(imp));
    }
  }, [gruposUsuario, grupoSeleccionPendienteId, grupoCaja?.codigo, total]);

  const cerrarSeleccionGrupo = useCallback(() => {
    setOpenSeleccionGrupo(false);
  }, []);

  const abrirSeleccionGrupo = useCallback(() => {
    if (gruposUsuario.length <= 1) return;
    setGrupoSeleccionPendienteId(grupoCaja?.id ?? null);
    setOpenSeleccionGrupo(true);
  }, [gruposUsuario.length, grupoCaja?.id]);

  useEffect(() => {
    if (!grupoCaja?.codigo) {
      setMediosEfectivo([]);
      setMediosTarjeta([]);
      setMediosTransferencia([]);
      setMediosOtros([]);
      return;
    }
    let cancelled = false;
    setLoadingMedios(true);
    (async () => {
      try {
        const [ef, ta, tr, ot] = await Promise.all([
          tesoreriaService.getMediosEfectivoPorGrupo(grupoCaja.codigo),
          tesoreriaService.getMediosTarjetaPorGrupo(grupoCaja.codigo),
          tesoreriaService.getMediosTransferenciaPorGrupo(grupoCaja.codigo),
          tesoreriaService.getMediosOtrosPorGrupo(grupoCaja.codigo),
        ]);
        if (cancelled) return;
        setMediosEfectivo(ef);
        setMediosTarjeta(ta);
        setMediosTransferencia(tr);
        setMediosOtros(ot);
      } catch {
        if (!cancelled) {
          setMediosEfectivo([]);
          setMediosTarjeta([]);
          setMediosTransferencia([]);
          setMediosOtros([]);
        }
      } finally {
        if (!cancelled) setLoadingMedios(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [grupoCaja?.codigo]);

  useEffect(() => {
    let cancelled = false;
    tesoreriaService
      .getMarcasTarjetas()
      .then((m) => {
        if (!cancelled) setMarcasTarjeta(m);
      })
      .catch(() => {
        if (!cancelled) setMarcasTarjeta([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const sumaCobrada = useMemo(
    () => round2(lineasCobro.reduce((acc, l) => acc + (Number(l.importe) || 0), 0)),
    [lineasCobro],
  );

  const restante = useMemo(() => round2(Math.max(0, total - sumaCobrada)), [total, sumaCobrada]);

  const draftMedio = useMemo(
    () =>
      resolveMedioDraft(
        draft,
        mediosEfectivo,
        mediosTarjeta,
        mediosTransferenciaFiltrados,
        mediosOtros,
      ),
    [draft, mediosEfectivo, mediosTarjeta, mediosTransferenciaFiltrados, mediosOtros],
  );

  useEffect(() => {
    setDraft((prev) => {
      const nextImporte = restante > 0 ? restante : total > 0 ? total : 0;
      if (prev.importe === nextImporte) return prev;
      return { ...prev, importe: nextImporte };
    });
  }, [restante, total]);

  const setDraftField = useCallback(<K extends keyof POSCobroDraft>(key: K, value: POSCobroDraft[K]) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  }, []);

  const setDraftTipoMedio = useCallback((tipo: POSMedioCobroTipo) => {
    setDraft((prev) => ({
      ...prev,
      tipo,
      tarjeta: tipo === 'tarjeta' ? { ...initialTarjeta } : prev.tarjeta,
      medioEfectivoId: tipo === 'efectivo' ? null : prev.medioEfectivoId,
      medioTarjetaId: tipo === 'tarjeta' ? null : prev.medioTarjetaId,
      medioTransferenciaId:
        tipo === 'transferencia' ? null : prev.medioTransferenciaId,
      medioOtrosId: tipo === 'otros' ? null : prev.medioOtrosId,
      fechaRegistro: tipo === 'otros' ? prev.fechaRegistro : undefined,
      nroComprobante: tipo === 'otros' ? prev.nroComprobante : undefined,
    }));
  }, []);

  const validarDraft = useCallback((): string | null => {
    const imp = round2(Number(draft.importe) || 0);
    if (imp <= 0) return 'Ingresá un importe mayor a cero.';
    if (imp > restante + 0.01) {
      return `El importe no puede superar el pend. de cobro (${restante.toFixed(2)}).`;
    }
    if (!draftMedio?.mcp) return 'Seleccioná un medio de cobro.';
    if (draft.tipo === 'tarjeta') {
      if (!draft.tarjeta.codigoMarca || draft.tarjeta.codigoMarca <= 0) {
        return 'Seleccioná la marca de tarjeta.';
      }
      if (!String(draft.tarjeta.numeroroAutorizacion ?? '').trim()) {
        return 'Ingresá el número de autorización.';
      }
    }
    if (draft.tipo === 'transferencia' && medioTransferenciaTieneCartera(draftMedio)) {
      const car = Number(draftMedio.cartera);
      if (!Number.isFinite(car) || car <= 0) {
        return 'Seleccioná la cuenta de transferencia.';
      }
    }
    return null;
  }, [draft, draftMedio, restante]);

  const agregarLineaCobro = useCallback((): string | null => {
    const err = validarDraft();
    if (err) return err;
    if (!draftMedio?.mcp) return 'Seleccioná un medio de cobro.';

    const imp = round2(Number(draft.importe) || 0);
    const nueva: POSLineaCobro = {
      id: genId(),
      tipo: draft.tipo,
      mcp: String(draftMedio.mcp).trim(),
      label: labelMedio(draftMedio),
      medioId: draftMedio.id,
      importe: imp,
    };

    if (draft.tipo === 'tarjeta') {
      nueva.tarjeta = { ...draft.tarjeta };
    }
    if (
      draft.tipo === 'transferencia' &&
      medioTransferenciaTieneCartera(draftMedio) &&
      Number(draftMedio.cartera) >= 1000
    ) {
      nueva.cartera = Number(draftMedio.cartera);
    }
    if (draft.tipo === 'otros') {
      const fr = (draft.fechaRegistro || '').trim();
      if (fr) nueva.fechaRegistro = fr;
      const nro = Number(draft.nroComprobante);
      if (Number.isFinite(nro) && nro > 0) nueva.nroComprobante = Math.trunc(nro);
    }

    setLineasCobro((prev) => [...prev, nueva]);
    const nuevoRestante = round2(Math.max(0, total - sumaCobrada - imp));
    setDraft(createDraft(nuevoRestante));
    return null;
  }, [validarDraft, draftMedio, draft, total, sumaCobrada]);

  /** Atajo: setea tipo, toma saldo pendiente y primer medio del tipo, y agrega la línea. */
  const agregarMedioPorTipo = useCallback(
    (tipo: POSMedioCobroTipo): string | null => {
      if (!cobroHabilitado) {
        return 'El cobro no está habilitado para esta forma de pago.';
      }
      if (restante <= 0.01) {
        return 'No hay saldo pendiente de cobro.';
      }
      const medios =
        tipo === 'efectivo'
          ? mediosEfectivo
          : tipo === 'tarjeta'
            ? mediosTarjeta
            : tipo === 'transferencia'
              ? mediosTransferenciaFiltrados
              : mediosOtros;
      const medio = medios[0] ?? null;
      if (!medio?.mcp) {
        return `No hay medios de ${tipo} en el grupo de caja.`;
      }
      if (tipo === 'tarjeta') {
        setDraftTipoMedio('tarjeta');
        setDraft((prev) => ({ ...prev, importe: restante }));
        return 'Completá marca y autorización de la tarjeta y agregá el medio.';
      }
      if (tipo === 'transferencia' && medioTransferenciaTieneCartera(medio)) {
        const car = Number(medio.cartera);
        if (!Number.isFinite(car) || car <= 0) {
          setDraftTipoMedio('transferencia');
          setDraft((prev) => ({ ...prev, importe: restante }));
          return 'Seleccioná la cuenta de transferencia y agregá el medio.';
        }
      }

      const imp = restante;
      const nueva: POSLineaCobro = {
        id: genId(),
        tipo,
        mcp: String(medio.mcp).trim(),
        label: labelMedio(medio),
        medioId: medio.id,
        importe: imp,
      };
      if (
        tipo === 'transferencia' &&
        medioTransferenciaTieneCartera(medio) &&
        Number(medio.cartera) >= 1000
      ) {
        nueva.cartera = Number(medio.cartera);
      }

      setLineasCobro((prev) => [...prev, nueva]);
      setDraft(createDraft(0));
      return null;
    },
    [
      cobroHabilitado,
      restante,
      mediosEfectivo,
      mediosTarjeta,
      mediosTransferenciaFiltrados,
      mediosOtros,
      setDraftTipoMedio,
    ],
  );

  const eliminarLineaCobro = useCallback((id: string) => {
    setLineasCobro((prev) => prev.filter((l) => l.id !== id));
  }, []);

  const validarCobro = useCallback((): string | null => {
    if (!cobroRequerido && lineasCobro.length === 0) return null;
    if (!cobroHabilitado) return null;
    if (openSeleccionGrupo) {
      return 'Seleccioná el grupo de caja para continuar.';
    }
    if (!grupoCaja?.codigo) {
      if (gruposUsuario.length === 0) {
        return 'No tenés grupos de caja asignados. Configurá el acceso con el administrador.';
      }
      if (gruposUsuario.length > 1) {
        return 'Seleccioná el grupo de caja para cobrar.';
      }
      return 'Configurá el grupo de caja del usuario antes de cobrar.';
    }
    if (total <= 0) return 'El total a cobrar debe ser mayor a cero.';
    if (lineasCobro.length === 0) {
      return 'Agregá al menos un medio de cobro.';
    }
    const diff = Math.abs(sumaCobrada - total);
    if (diff > 0.02) {
      if (sumaCobrada < total) {
        return `Faltan ${round2(total - sumaCobrada).toFixed(2)} para cubrir el total.`;
      }
      return `Los medios suman ${sumaCobrada.toFixed(2)} y el total es ${total.toFixed(2)}.`;
    }
    for (const linea of lineasCobro) {
      if (linea.tipo === 'tarjeta' && linea.tarjeta) {
        if (!linea.tarjeta.codigoMarca || linea.tarjeta.codigoMarca <= 0) {
          return `Marca de tarjeta requerida en ${linea.label}.`;
        }
        if (!String(linea.tarjeta.numeroroAutorizacion ?? '').trim()) {
          return `Autorización requerida en ${linea.label}.`;
        }
      }
    }
    if (restante > 0.02) {
      return `Agregá medios por ${restante.toFixed(2)} o confirmá el borrador pendiente.`;
    }
    return null;
  }, [
    cobroRequerido,
    cobroHabilitado,
    grupoCaja,
    openSeleccionGrupo,
    gruposUsuario.length,
    total,
    lineasCobro,
    sumaCobrada,
    restante,
  ]);

  const buildReciboPayload = useCallback(
    (
      factura: {
        nroFormulario: number;
        importeTotal?: number;
        letra?: string;
        nroSuc?: number;
        nroComprobante?: number;
      },
      comprobanteLabel: string,
    ): ReciboCreatePayload | null => {
      if (!cobroHabilitado || !grupoCaja?.codigo || !nroCliente || nroCliente <= 0) {
        return null;
      }
      if (lineasCobro.length === 0) return null;

      const monto = round2(
        Number(factura.importeTotal) > 0 ? Number(factura.importeTotal) : total,
      );
      const mediosDeCobro = ajustarMediosAlMontoFactura(
        lineasCobro.map(lineaToMedioApi),
        monto,
      );

      const fechaIso = fechaEmision?.trim()
        ? new Date(`${fechaEmision.trim()}T12:00:00`).toISOString()
        : new Date().toISOString();

      return {
        nroCliente,
        nroSuc: nroSuc > 0 ? nroSuc : Number(factura.nroSuc) || 0,
        grupoMCP: grupoCaja.codigo,
        fechaEmision: fechaIso,
        descripcion: `Cobro POS FC ${comprobanteLabel}`.trim(),
        mediosDeCobro,
        documentosAplicar: [
          {
            nroIdformulario: factura.nroFormulario,
            montoCancelado: monto,
          },
        ],
      };
    },
    [cobroHabilitado, grupoCaja, nroCliente, nroSuc, lineasCobro, total, fechaEmision],
  );

  const crearReciboAplicado = useCallback(
    async (
      factura: {
        nroFormulario: number;
        importeTotal?: number;
        letra?: string;
        nroSuc?: number;
        nroComprobante?: number;
      },
      comprobanteLabel: string,
    ) => {
      if (!cobroHabilitado) {
        throw new Error(
          'La forma de pago no admite cobro en POS (p. ej. CTA.CTE.). Registrá el recibo en tesorería si corresponde.',
        );
      }
      const payload = buildReciboPayload(factura, comprobanteLabel);
      if (!payload) {
        throw new Error(
          'No se pudo armar el recibo de cobro. Verificá grupo de caja, cliente y medios cargados.',
        );
      }
      return tesoreriaService.createRecibo(payload);
    },
    [buildReciboPayload, cobroHabilitado],
  );

  const resetCobro = useCallback(() => {
    setLineasCobro([]);
    setDraft(createDraft(total > 0 ? total : 0));
  }, [total]);

  const puedeAgregarMedio = restante > 0.01 && !loadingMedios;

  const tieneMedios =
    mediosEfectivo.length > 0 ||
    mediosTarjeta.length > 0 ||
    mediosTransferenciaFiltrados.length > 0 ||
    mediosOtros.length > 0;

  const resumenMediosLabel =
    lineasCobro.length > 0
      ? lineasCobro.map((l) => `${l.label} ${formatCurrencyShort(l.importe)}`).join(' · ')
      : undefined;

  return {
    cobroHabilitado,
    cobroRequerido,
    grupoCaja,
    gruposUsuario,
    openSeleccionGrupo,
    grupoSeleccionPendienteId,
    setGrupoSeleccionPendienteId,
    confirmarGrupoSeleccion,
    cerrarSeleccionGrupo,
    abrirSeleccionGrupo,
    loadingGrupo,
    loadingMedios,
    mediosEfectivo,
    mediosTarjeta,
    mediosTransferencia: mediosTransferenciaFiltrados,
    mediosOtros,
    marcasTarjeta,
    lineasCobro,
    eliminarLineaCobro,
    draft,
    setDraftField,
    setDraftTipoMedio,
    agregarLineaCobro,
    agregarMedioPorTipo,
    validarDraft,
    sumaCobrada,
    restante,
    draftMedio,
    puedeAgregarMedio,
    validarCobro,
    buildReciboPayload,
    crearReciboAplicado,
    resetCobro,
    tieneMedios,
    resumenMediosLabel,
  };
}

function formatCurrencyShort(n: number): string {
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: 2,
  }).format(n);
}

export default usePosCobro;
